import React from "react";
import {
  Button,
  Divider,
  FormFieldSet,
  Modal,
  PageHeader,
  PageTitle,
  useFormHandler,
} from "../../../../../../components";
import { v4 as uuidv4 } from "uuid";
import {
  documentTypeOptions,
  placementOptions,
} from "../../../../../../assets/data/projectData";
import { RecordStatus } from "../../../../../../assets/data/enumData";
import { useLoginUser } from "../../../../../../hooks/useLoginUser";
import { useDispatch, useSelector } from "react-redux";
import { uploadStart } from "../../../../../../redux/slices/universal/fileUploadSlice";
import { addDocumentStart } from "../../../../../../redux/slices/universal/documentsSlice";
import { getplacementDocumentsStart } from "../../../../../../redux/slices/document/documentSlice";

const VendorDocumetsModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const documents = useSelector((state) => state.documents.taxForm) || {};
  const {
    employeeProfileUiid,
    currentEmployee: { employeeDetails, loadedTabs },
    loading,
    error,
    employees,
    totalCount,
  } = useSelector((state) => state.employee);
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const tableData = documents?.vendorPlacement || [];
  const { placements } = employeeDetails || {};
  const formatted = tableData.map((item) => ({
    label: `${item?.jobInfo?.jobTitle}-${item?.jobInfo?.jobCode || ""}-${
      employeeDetails?.firstName
    }`,
    value: item?.uiid,
  }));
  const {
    formData,
    errors,
    handleChange,
    setFormData,
    setErrors,
    validateAllFields,
  } = useFormHandler(
    {
      documentType: "",
      selectPlacement: "",
      attachments: [
        {
          id: uuidv4(),
          // title: "",
          // comments: "",
          // status: "",
          // expiryDate: "",
          chooseFiles: [],
        },
      ],
    },
    {
      documentType: { type: "text", required: true },
      selectPlacement: { type: "select", required: true, number: true },
      attachments: [
        {
          title: { type: "text", required: true },
          comments: { type: "text", required: true },
          status: { type: "select", required: true },
          chooseFiles: {
            type: "file",
            required: true,
            // allowedFiles: [
            //   { ext: "png", mime: "image/png" },
            //   { ext: "txt", mime: "text/plain" },
            // ],
          },
        },
      ],
    },
  );
  const handleAddRow = () => {
    // Store current errors
    const currentErrors = { ...errors };

    // Update formData directly instead of using handleChange
    setFormData({
      ...formData,
      attachments: [
        ...formData.attachments,
        {
          id: uuidv4(),
          title: "",
          comments: "",
          status: "",
          expiryDate: "",
          chooseFiles: [],
        },
      ],
    });

    // Make sure to preserve errors
    setErrors(currentErrors);
  };

  const handleRemoveRow = (id) => {
    if (formData.attachments.length > 1) {
      // Find the index of the row to be removed
      const indexToRemove = formData.attachments.findIndex(
        (row) => row.id === id,
      );

      // Update formData directly
      const newDocumentRows = formData.attachments.filter(
        (row) => row.id !== id,
      );

      setFormData({
        ...formData,
        attachments: newDocumentRows,
      });

      // Update the errors state to remove errors for the deleted row
      if (errors?.attachments) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };

          if (newErrors.attachments) {
            // Remove the errors for the removed index
            const updatedRowErrors = [...newErrors.attachments];
            updatedRowErrors.splice(indexToRemove, 1);
            newErrors.attachments = updatedRowErrors;
          }

          return newErrors;
        });
      }
    }
  };

  const getAttachmentError = (index, field) => {
    return errors?.attachments?.[index]?.[field] || "";
  };

  const handleFieldChange = (index, field, value) => {
    handleChange(`attachments[${index}].${field}`, value);
  };

  const handleSave = () => {
    const isValid = validateAllFields();
    const checkType =
      tableData.find((data) => data.uiid === formData?.selectPlacement)?.type ??
      null;
    if (isValid && checkType) {
      // Filter valid document entries
      const cleanAttachments = formData.attachments.filter(
        (att) =>
          att.title.trim() !== "" ||
          att.status.trim() !== "" ||
          (att.chooseFiles && att.chooseFiles.length > 0),
      );
      // Map files with their parent doc id
      const cleanAttachmentsFiles = cleanAttachments.flatMap((doc) =>
        doc.chooseFiles.map((file) => {
          file.id = doc.id;
          return file;
        }),
      );
      if (!cleanAttachmentsFiles.length) return;
      dispatch(
        uploadStart({
          uploadType: "multiple",
          field: "doc",
          uiid: formData?.selectPlacement,
          entity: "placements",
          category: "Documents",
          coll: "Placement",
          doc: cleanAttachmentsFiles,
          onSuccess: (fileData) => {
            const attachmentsPayload = cleanAttachments.map((clean) => {
              return {
                addedBy: loginUserUiid,
                docType: formData?.documentType,
                parent: { employee: employeeProfileUiid },
                ref: {
                  id: formData?.selectPlacement,
                  entity: "Placement",
                  path:
                    checkType === 1
                      ? "clientPlacement.documents.placement"
                      : checkType === 2
                        ? "permanentPlacement.documents"
                        : "",
                },
                file: (clean.chooseFile || []).map((file) => {
                  const matchedFile = fileData.find(
                    (f) => f?.fileRefId === clean.id && f.key,
                  );
                  return {
                    name: clean.documentName,
                    comments: clean.comments,
                    status: clean.status,
                    docExpiry: clean.expiryDate,
                    keys: matchedFile ? [matchedFile.key] : [],
                  };
                }),
              };
            });
            dispatch(
              addDocumentStart({
                coll: "Document",
                refColl: "Placement",
                refId: formData?.selectPlacement,
                refField:
                  checkType === 1
                    ? "clientPlacement.documents.placement"
                    : checkType === 2
                      ? "permanentPlacement.documents"
                      : "",
                refPatchId: "",
                payload: {
                  addedBy: loginUserUiid,
                  attachments: attachmentsPayload,
                },
                onSuccess: async () => {
                  dispatch(
                    getplacementDocumentsStart({
                      fil: {
                        uiid: { type: "IN", value: placements },
                        "jobInfo.entityType": { type: "NONE", value: "Vendor" },
                      },
                      cache: false,
                      ret: "vendorPlacement",
                      ref: "Document",
                    }),
                  );
                  onClose();
                  // handleModalClose(); // Optional: uncomment if you want to close the modal on success
                },
              }),
            );
          },
        }),
      );
    } else {
      console.log("Please fix the errors before submitting.", errors);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="ADD" width="max-w-7xl">
      <div>
        {/* <div className="grid grid-cols-3 gap-x-8 gap-y-6 px-2 max-w-7xl"> */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4 px-2 max-w-7xl">
          <FormFieldSet
            type="select"
            title="Document Type"
            placeholder="Select Document Type"
            value={formData.documentType}
            onChange={(value) => handleChange("documentType", value)}
            error={errors.documentType}
            showButton
            showSearch
            options={documentTypeOptions}
          />
          <FormFieldSet
            type="select"
            title="Select Placement"
            placeholder="Select Placement"
            value={formData.selectPlacement}
            onChange={(value) => handleChange("selectPlacement", value)}
            error={errors.selectPlacement}
            showSearch
            options={formatted || []}
          />
        </div>

        {/* Attachments Section */}
        <div className="mt-4 mb-1">
          <PageHeader
            Section={
              <div className="flex">
                <PageTitle title="Attachments" tag="h6" />
              </div>
            }
          />
        </div>
        <Divider />

        {formData.attachments.map((row, index) => (
          <div key={row.id} className="flex gap-2 items-center px-1 py-4 ">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-x-4 gap-y-4 w-full">
              <FormFieldSet
                type="text"
                placeholder="Title"
                title=""
                noTitle
                value={row.title}
                onChange={(value) => handleFieldChange(index, "title", value)}
                error={getAttachmentError(index, "title")}
              />
              <FormFieldSet
                type="text"
                title=""
                noTitle
                placeholder="Comments"
                onChange={(value) =>
                  handleFieldChange(index, "comments", value)
                }
                value={row.comments}
                error={getAttachmentError(index, "comments")}
              />
              <FormFieldSet
                type="select"
                placeholder="Status"
                title=""
                noTitle
                value={row.status}
                onChange={(value) => handleFieldChange(index, "status", value)}
                error={getAttachmentError(index, "status")}
                options={RecordStatus}
              />
              <FormFieldSet
                type="datepicker"
                title=""
                noTitle
                placeholder="Expiry Date"
                value={row.expiryDate}
                onChange={(value) =>
                  handleFieldChange(index, "expiryDate", value)
                }
                error={getAttachmentError(index, "expiryDate")}
                showDropdown={false}
              />
              <FormFieldSet
                type="file"
                title=""
                noTitle
                placeholder="Choose File"
                value={row.chooseFiles}
                onChange={(value) =>
                  handleFieldChange(index, "chooseFiles", value)
                }
                error={getAttachmentError(index, "chooseFiles")}
              />
            </div>
            <div className="flex gap-2 items-center">
              <Button
                type="icon"
                size="xs"
                icon="add"
                color="black"
                onClick={handleAddRow}
              />
              <Button
                type="icon"
                size="xs"
                icon="remove"
                color="danger"
                onClick={() => handleRemoveRow(row.id)}
                disabled={formData.attachments.length === 1}
              />
            </div>
          </div>
          // <div key={row.id} className="flex gap-4 px-1 py-4 w-full items-start">
          //   <div className="flex-1">
          //     <FormFieldSet
          //       type="text"
          //       placeholder="Title"
          //       title=""
          //       value={row.title}
          //       onChange={(value) => handleFieldChange(index, "title", value)}
          //       error={getAttachmentError(index, "title")}
          //     />
          //   </div>
          //   <div className="flex-1">
          //     <FormFieldSet
          //       type="text"
          //       title=""
          //       placeholder="Comments"
          //       onChange={(value) =>
          //         handleFieldChange(index, "comments", value)
          //       }
          //       value={row.comments}
          //       error={getAttachmentError(index, "comments")}
          //     />
          //   </div>
          //   <div className="flex-1">
          //     <FormFieldSet
          //       type="select"
          //       placeholder="Status"
          //       title=""
          //       value={row.status}
          //       onChange={(value) => handleFieldChange(index, "status", value)}
          //       error={getAttachmentError(index, "status")}
          //       options={RecordStatus}
          //     />
          //   </div>
          //   <div className="flex-1">
          //     <FormFieldSet
          //       type="datepicker"
          //       title=""
          //       placeholder="Expiry Date"
          //       value={row.expiryDate}
          //       onChange={(value) =>
          //         handleFieldChange(index, "expiryDate", value)
          //       }
          //       error={getAttachmentError(index, "expiryDate")}
          //     />
          //   </div>
          //   <div className="flex-1 flex items-center gap-2 ">
          //     <FormFieldSet
          //       type="file"
          //       title=""
          //       placeholder="Choose File"
          //       value={row.chooseFiles}
          //       onChange={(value) =>
          //         handleFieldChange(index, "chooseFiles", value)
          //       }
          //       error={getAttachmentError(index, "chooseFiles")}
          //     />
          //     <div className="flex gap-2 mt-5 ">
          //       <Button
          //         type="icon"
          //         size="xs"
          //         icon="add"
          //         color="black"
          //         onClick={handleAddRow}
          //       />
          //       <Button
          //         type="icon"
          //         size="xs"
          //         icon="remove"
          //         color="danger"
          //         onClick={() => handleRemoveRow(row.id)}
          //         disabled={formData.attachments.length === 1}
          //       />
          //     </div>
          //   </div>
          // </div>
        ))}
        <div className="flex gap-3 justify-end my-6">
          <Button title="Cancel" variant="outlined" onClick={onClose} />
          <Button title="Save" onClick={() => handleSave()} />
        </div>
      </div>
    </Modal>
  );
};

export default VendorDocumetsModal;
